import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useGateValue } from '@statsig/react-bindings'

import {
  Company,
  FeatureFlagValue,
  KYCMode,
  LegalPerson,
  SignUpFeedbackStatus,
  Situation,
  UserSituationStatus,
  UserSituationStatusEnum,
} from '@types'

import {
  STORE_STATUS_FINISHED,
  STORE_STATUS_INITIALIZED,
  STORE_STATUS_LOADING,
} from 'constants/store'
import {
  URL_BIOMETRIC,
  URL_CHOOSE_IDENTITY_VALIDATION,
  URL_FEEDBACK,
  URL_ONBOARDING,
  URL_QUIZ,
  URL_REJECT,
  URL_SIGN_UP,
  URL_TERMS,
  URL_VERIFY_PHONE,
} from 'constants/urls'

import { useDispatch, useSelector } from 'stores'
import { useService } from 'services'
import { useQuery, useRouter } from 'modules/navigation'
import { fetchUserSituation } from '../stores/situation.store'

export function useSituation() {
  const { query } = useQuery()
  const dispatch = useDispatch()
  const userDetail = useSelector((state) => state.user.detail)
  const userSituation = useSelector((state) => state.user.situation)
  const { user: service } = useService()
  const router = useRouter()
  const supplierID = query.get('supplier_id')

  const isCompratinoQuizValidation = useGateValue(FeatureFlagValue.QUIZ_KYC)

  const kycMode: KYCMode = useMemo(
    () => userSituation.data.kyc_mode ?? 'IDUnico',
    [userSituation.data]
  )

  const status: UserSituationStatus = useMemo(() => {
    const company = userSituation.data.legal_person_situation.find(
      (l) =>
        l.document_number === userDetail.data.currentCompany.document_number
    )
    if (
      company &&
      (company.legal_entity_document_required ||
        company.power_of_attorney_upload_required)
    )
      return UserSituationStatusEnum.Waiting

    if (
      ['waiting_analysis', 'sent_documents', 'waiting_provider'].includes(
        userSituation.data.status
      )
    )
      return UserSituationStatusEnum.Analysis

    if (
      ['waiting_docs', 'onboarded'].includes(userSituation.data.status) &&
      kycMode === 'quiz' &&
      !userSituation.data.has_facial_recognition &&
      supplierID &&
      !isCompratinoQuizValidation
    ) {
      return UserSituationStatusEnum.Waiting
    }

    if (
      userSituation.data.status === 'onboarded' ||
      userSituation.data.status === 'verified'
    )
      return UserSituationStatusEnum.Onboarded

    if (userSituation.data.status === 'unauthorized')
      return UserSituationStatusEnum.Unauthorized

    if (userSituation.data.status === 'waiting_docs')
      return UserSituationStatusEnum.Waiting

    return UserSituationStatusEnum.Unknown
  }, [userSituation.data, userDetail.data.currentCompany.document_number])

  /**
   * This memo checks if the user has started membership proof's process but is waiting for approval
   */
  const isPowerOfAttorneyPending = useMemo(() => {
    const company = userSituation.data.legal_person_situation.find(
      (l) =>
        l.document_number === userDetail.data.currentCompany.document_number
    )

    return company?.power_of_attorney_required || false
  }, [
    userSituation.data,
    userDetail.data.currentCompany.document_number,
    userDetail.data.userValidatedByOwner,
  ])

  /**
   * This memo checks if the user hasn't started membership proof's process
   */
  const isMissingPowerOfAttorneyDocuments = useMemo(() => {
    const company = userSituation.data.legal_person_situation.find(
      (l) =>
        l.document_number === userDetail.data.currentCompany.document_number
    )

    return company?.power_of_attorney_upload_required || false
  }, [
    userSituation.data,
    userDetail.data.currentCompany.document_number,
    userDetail.data.userValidatedByOwner,
  ])

  const { pathname } = useLocation()

  function convertLegalPersonToCompany(legalPerson: LegalPerson[]): Company[] {
    return legalPerson.map((lp) => ({
      id: lp.document_number,
      document_number: lp.document_number,
      document_type: lp.document_type,
      name: lp.name,
      is_buyer: true,
      is_legal_person: true,
    }))
  }

  function fetch(interceptor?: (response: Situation) => void) {
    const response = service.situation().then((response) => {
      interceptor && interceptor(response)
      return response
    })
    dispatch(fetchUserSituation(response))
    return response
  }

  function isSituationReady() {
    return userSituation.status !== STORE_STATUS_INITIALIZED
  }

  const isSituationLoading = userSituation.status === STORE_STATUS_LOADING

  function isLoading() {
    return (
      userSituation.status !== STORE_STATUS_FINISHED &&
      !userSituation.data.legal_person_situation.length
    )
  }

  function redirectTo(
    fallback?: () => void,
    situation: Situation = userSituation.data
  ) {
    if (situation.status === 'rejected') {
      return router.pushPreserveSearch(`${URL_ONBOARDING}${URL_REJECT}`)
    }

    if (
      ['verified', 'onboarded'].includes(situation.status) &&
      !userDetail.data.userValidatedByOwner &&
      !userDetail.data.currentCompany.is_seller
    ) {
      if (isMissingPowerOfAttorneyDocuments) {
        return router.pushPreserveSearch(
          `${URL_SIGN_UP}${URL_FEEDBACK}/${SignUpFeedbackStatus.NOT_OWNER}`
        )
      }

      if (isPowerOfAttorneyPending) {
        return router.pushPreserveSearch(
          `${URL_SIGN_UP}${URL_FEEDBACK}/${SignUpFeedbackStatus.DOCUMENTS_ANALYSIS}`
        )
      }
    }

    if (
      ['waiting_docs', 'onboarded'].includes(userSituation.data.status) &&
      kycMode === 'quiz' &&
      !situation.has_facial_recognition &&
      supplierID &&
      !isCompratinoQuizValidation
    ) {
      return router.pushPreserveSearch(`${URL_SIGN_UP}${URL_BIOMETRIC}`)
    }

    // Current flow that should be refactored to work with the new SignUpV2
    if (situation.has_pending_terms_to_sign) {
      return router.pushPreserveSearch(`${URL_ONBOARDING}${URL_TERMS}`)
    }

    if (situation.status === 'waiting_whatsapp') {
      return router.pushPreserveSearch(`${URL_SIGN_UP}${URL_VERIFY_PHONE}`)
    }

    if (situation.status === 'waiting_docs' && kycMode === 'quiz') {
      return router.pushPreserveSearch(`${URL_SIGN_UP}${URL_QUIZ}`)
    }

    if (situation.status === 'waiting_docs' && kycMode === 'choose_type') {
      if (!pathname.includes(URL_BIOMETRIC)) {
        return router.pushPreserveSearch(
          `${URL_SIGN_UP}${URL_CHOOSE_IDENTITY_VALIDATION}`
        )
      }
      return router.pushPreserveSearch(`${URL_SIGN_UP}${URL_BIOMETRIC}`)
    }

    if (situation.status === 'waiting_docs' && kycMode === 'IDUnico') {
      return router.pushPreserveSearch(`${URL_SIGN_UP}${URL_BIOMETRIC}`)
    }

    fallback?.()
  }

  return {
    kycMode,
    status,
    situation: userSituation,
    fetch,
    convertLegalPersonToCompany,
    isSituationReady,
    isLoading,
    isSituationLoading,
    redirectTo,
    isPowerOfAttorneyPending,
    isMissingPowerOfAttorneyDocuments,
  }
}
